import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import Box from '@material-ui/core/Box';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';
import Typography from '@material-ui/core/Typography';
import BusinessIcon from '@material-ui/icons/Business';
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import PersonIcon from '@material-ui/icons/Person';
import {
  $createTextNode,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_LOW,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
} from 'lexical';
import { useCallback, useRef, useState, useEffect } from 'react';
import {
  getMentions,
  getMentorMentions,
  getFounderMentions,
  getVentureMentions,
  MentionSuggestion,
  MentionType,
} from '../../../api/mentions';
import { COLORS } from '../../../theme/variables';
import { $createMentionNode, $isMentionNode } from '../nodes/mention-node';

class MentionOption extends MenuOption {
  item: MentionSuggestion;
  constructor(item: MentionSuggestion) {
    super(item.id || Math.random().toString(36).slice(2));
    this.item = item;
  }
}

export enum MentionSource {
  Mentor = 'Mentor',
  Founder = 'Founder',
  Venture = 'Venture',
  All = 'All',
}

type Props = {
  minChars?: number; // default 2
  debounceMs?: number; // default 200
  maxSuggestions?: number; // default 30
  source?: MentionSource | null;
};

function getMentionsFetchFunction(source: MentionSource) {
  switch (source) {
    case MentionSource.Mentor:
      return getMentorMentions;
    case MentionSource.Founder:
      return getFounderMentions;
    case MentionSource.Venture:
      return getVentureMentions;
    case MentionSource.All:
    default:
      return getMentions;
  }
}

function MentionPlugin({
  minChars = 2,
  debounceMs = 200,
  maxSuggestions = 30,
  source,
}: Props) {
  const [editor] = useLexicalComposerContext();

  // No need to register special nodes - we'll use regular links
  const [options, setOptions] = useState<MentionOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [isVisible, setIsVisible] = useState(false);

  const renderTypeIcon = (type: MentionType) => {
    switch (type) {
      case MentionType.MENTOR:
        return (
          <PersonIcon
            style={{ color: COLORS.COLOR_GREEN_BASE, fontSize: 18 }}
          />
        );
      case MentionType.FOUNDER:
        return (
          <EmojiObjectsIcon
            style={{ color: COLORS.COLOR_BLUE_BASE, fontSize: 18 }}
          />
        );
      case MentionType.VENTURE:
        return (
          <BusinessIcon
            style={{ color: COLORS.COLOR_ADDITIONAL_ORANGE, fontSize: 18 }}
          />
        );
      default:
        return (
          <PersonIcon style={{ color: COLORS.COLOR_GRAY_BASE, fontSize: 18 }} />
        );
    }
  };

  const lastReqId = useRef(0);
  const prevQueryRef = useRef('');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortController = useRef<AbortController | null>(null);

  const fetchSuggestions = useCallback(
    async (q: string) => {
      // Don't fetch if source is null or undefined
      if (!source) {
        setOptions([]);
        setIsLoading(false);
        return;
      }

      // Cancel previous request
      if (abortController.current) {
        abortController.current.abort();
      }

      abortController.current = new AbortController();
      const reqId = ++lastReqId.current;

      setIsLoading(true);

      try {
        const fetchMentions = getMentionsFetchFunction(source);
        const { suggestions } = await fetchMentions(q);

        if (reqId !== lastReqId.current) return;

        const limitedSuggestions = (suggestions ?? []).slice(0, maxSuggestions);
        setOptions(limitedSuggestions.map((s) => new MentionOption(s)));
      } catch (error) {
        if (reqId !== lastReqId.current) return;
        console.warn('Failed to fetch mentions:', error);
        setOptions([]);
      } finally {
        setIsLoading(false);
      }
    },
    [maxSuggestions, source],
  );

  const handleQueryChange = useCallback(
    (q: string | null) => {
      // If source is null or undefined, don't fetch anything
      if (!source) {
        setOptions([]);
        setIsLoading(false);
        setQuery('disabled');
        return;
      }

      const trimmed = (q ?? '').trim();

      // Deduplicate: avoid re-fetching for the same query when caret moves
      if (trimmed === prevQueryRef.current) {
        return;
      }
      prevQueryRef.current = trimmed;

      if (timer.current) clearTimeout(timer.current);

      setQuery(trimmed);

      if (trimmed.length < minChars) {
        setOptions([]);
        setIsLoading(false);
        return;
      }

      timer.current = setTimeout(() => fetchSuggestions(trimmed), debounceMs);
    },
    [debounceMs, minChars, fetchSuggestions, source],
  );

  useEffect(() => {
    const currentReqId = lastReqId.current;
    return () => {
      if (timer.current) clearTimeout(timer.current);
      if (abortController.current) abortController.current.abort();
      lastReqId.current = currentReqId + 1;
    };
  }, []);

  // Handle editor focus/blur to show/hide dropdown
  useEffect(() => {
    const handleFocus = () => {
      setIsVisible(true);
    };

    const handleBlur = () => {
      setIsVisible(false);
      setIsLoading(false);
    };

    const editorElement = editor.getRootElement();
    if (editorElement) {
      editorElement.addEventListener('focus', handleFocus);
      editorElement.addEventListener('blur', handleBlur);
      return () => {
        editorElement.removeEventListener('focus', handleFocus);
        editorElement.removeEventListener('blur', handleBlur);
      };
    }
  }, [editor]);

  // Handle keyboard commands for atomic mention deletion
  useEffect(() => {
    const removeListener = editor.registerCommand(
      KEY_BACKSPACE_COMMAND,
      (event) => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) {
          return false;
        }

        const anchorNode = selection.anchor.getNode();
        const parent = anchorNode.getParent();

        // Check if we're at the beginning of a mention node
        if ($isMentionNode(parent) && selection.anchor.offset === 0) {
          event?.preventDefault();
          const prevSibling = parent.getPreviousSibling();
          parent.remove();
          // Position cursor at the end of previous node or at start of parent
          if ($isTextNode(prevSibling)) {
            const size = prevSibling.getTextContentSize();
            prevSibling.select(size, size);
          }
          return true;
        }

        // Check if we're in a text node right after a mention
        if (anchorNode.getType() === 'text' && selection.anchor.offset === 0) {
          const prevSibling = anchorNode.getPreviousSibling();
          if ($isMentionNode(prevSibling)) {
            event?.preventDefault();
            const beforeMention = prevSibling.getPreviousSibling();
            prevSibling.remove();
            // If current text node is only zero-width space, remove it too
            if (
              $isTextNode(anchorNode) &&
              anchorNode.getTextContent() === '\u200B'
            ) {
              anchorNode.remove();
            }
            // Position cursor at the end of the node before mention
            if ($isTextNode(beforeMention)) {
              const size = beforeMention.getTextContentSize();
              beforeMention.select(size, size);
            } else if ($isTextNode(anchorNode)) {
              anchorNode.select(0, 0);
            }
            return true;
          }
        }

        // Check if we're trying to delete a zero-width space right after a mention
        if ($isTextNode(anchorNode) && selection.anchor.offset === 1) {
          const text = anchorNode.getTextContent();
          if (text.startsWith('\u200B')) {
            const prevSibling = anchorNode.getPreviousSibling();
            if ($isMentionNode(prevSibling)) {
              event?.preventDefault();
              const beforeMention = prevSibling.getPreviousSibling();
              // Delete both the mention and the zero-width space
              prevSibling.remove();
              if (text.length === 1) {
                anchorNode.remove();
                // Position cursor at the end of the node before mention
                if ($isTextNode(beforeMention)) {
                  const size = beforeMention.getTextContentSize();
                  beforeMention.select(size, size);
                }
              } else {
                anchorNode.setTextContent(text.slice(1));
                anchorNode.select(0, 0);
              }
              return true;
            }
          }
        }

        return false;
      },
      COMMAND_PRIORITY_LOW,
    );

    const deleteListener = editor.registerCommand(
      KEY_DELETE_COMMAND,
      (event) => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) {
          return false;
        }

        const anchorNode = selection.anchor.getNode();
        const parent = anchorNode.getParent();

        // Check if we're at the end of a mention node
        if (
          $isMentionNode(parent) &&
          selection.anchor.offset === parent.getTextContentSize()
        ) {
          event?.preventDefault();
          // Remove mention and the next zero-width space if it exists
          const nextSibling = parent.getNextSibling();
          const prevSibling = parent.getPreviousSibling();
          parent.remove();
          if (
            $isTextNode(nextSibling) &&
            nextSibling.getTextContent() === '\u200B'
          ) {
            nextSibling.remove();
          }
          // Position cursor at the end of previous node
          if ($isTextNode(prevSibling)) {
            const size = prevSibling.getTextContentSize();
            prevSibling.select(size, size);
          }
          return true;
        }

        // Check if we're in a text node right before a mention
        if (
          anchorNode.getType() === 'text' &&
          selection.anchor.offset === anchorNode.getTextContentSize()
        ) {
          const nextSibling = anchorNode.getNextSibling();
          if ($isMentionNode(nextSibling)) {
            event?.preventDefault();
            // Remove mention and any zero-width space after it
            const afterMention = nextSibling.getNextSibling();
            const currentOffset = anchorNode.getTextContentSize();
            nextSibling.remove();
            if (
              $isTextNode(afterMention) &&
              afterMention.getTextContent() === '\u200B'
            ) {
              afterMention.remove();
            }
            // Keep cursor at current position
            anchorNode.select(currentOffset, currentOffset);
            return true;
          }
        }

        // Check if we're at the start of a text node that begins with zero-width space after mention
        if ($isTextNode(anchorNode) && selection.anchor.offset === 0) {
          const text = anchorNode.getTextContent();
          if (text.startsWith('\u200B')) {
            const prevSibling = anchorNode.getPreviousSibling();
            if ($isMentionNode(prevSibling)) {
              event?.preventDefault();
              const beforeMention = prevSibling.getPreviousSibling();
              // Delete both the mention and the zero-width space
              prevSibling.remove();
              if (text.length === 1) {
                anchorNode.remove();
                // Position cursor at the end of the node before mention
                if ($isTextNode(beforeMention)) {
                  const size = beforeMention.getTextContentSize();
                  beforeMention.select(size, size);
                }
              } else {
                anchorNode.setTextContent(text.slice(1));
                anchorNode.select(0, 0);
              }
              return true;
            }
          }
        }

        return false;
      },
      COMMAND_PRIORITY_LOW,
    );

    return () => {
      removeListener();
      deleteListener();
    };
  }, [editor]);

  return (
    <LexicalTypeaheadMenuPlugin<MentionOption>
      onQueryChange={handleQueryChange}
      onSelectOption={(option, nodeToReplace, closeMenu) => {
        editor.update(() => {
          const mentionNode = $createMentionNode(option.item);

          if (nodeToReplace) {
            // Get the text content to check for leading space
            const textContent = nodeToReplace.getTextContent();
            const hasLeadingSpace = textContent.startsWith(' ');

            if (hasLeadingSpace) {
              // If there was a leading space, we need to preserve it
              // Split the text node: keep the space, replace the rest with mention
              const spaceNode = $createTextNode(' ');
              const parent = nodeToReplace.getParent();

              if (parent) {
                // Insert space before mention
                nodeToReplace.insertBefore(spaceNode);
                // Replace the rest with mention
                nodeToReplace.replace(mentionNode);

                // Add zero-width space and regular space after mention
                const afterSpaceNode = $createTextNode('\u200B ');
                mentionNode.insertAfter(afterSpaceNode);
                // Move cursor after the space
                afterSpaceNode.select(2, 2);
              }
            } else {
              // No leading space, just replace and add space after
              nodeToReplace.replace(mentionNode);

              const spaceNode = $createTextNode('\u200B ');
              mentionNode.insertAfter(spaceNode);
              spaceNode.select(2, 2);
            }
          } else {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
              selection.insertNodes([mentionNode]);
              const spaceNode = $createTextNode('\u200B ');
              mentionNode.insertAfter(spaceNode);
              spaceNode.select(2, 2);
            }
          }
        });

        closeMenu();
      }}
      triggerFn={(text) => {
        // Check if we're inside an existing mention node
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          const anchorNode = selection.anchor.getNode();
          const parent = anchorNode.getParent();

          // Check if we're inside a mention node
          if (parent && parent.getType() === 'mention') {
            return null;
          }

          // Check if we're inside a text node that's inside a mention
          if (anchorNode.getType() === 'text') {
            let currentParent = anchorNode.getParent();
            while (currentParent) {
              if (currentParent.getType() === 'mention') {
                return null;
              }
              currentParent = currentParent.getParent();
            }
          }
        }

        // Trigger @ mentions after: start of line, spaces, punctuation, special chars
        // But NOT after letters or numbers (prevents triggering mid-word or in emails)
        // Allow one space in the mention text for names like "John Doe"
        // \p{L} = any Unicode letter, \p{N} = any Unicode number (works for all languages)
        const mentionRegex = new RegExp(
          '(^|[^\\p{L}\\p{N}])@([^\\s]*(?:\\s[^\\s]*)?)$',
          'u',
        );
        const match = mentionRegex.exec(text);
        if (!match) return null;
        const matching = match[2] ?? '';

        return {
          leadOffset: match[0].length - matching.length,
          matchingString: matching,
          replaceableString: match[0],
        };
      }}
      options={options}
      menuRenderFn={(
        anchorElementRef,
        { selectedIndex, selectOptionAndCleanUp, options },
      ) => {
        const anchorEl = anchorElementRef?.current;
        if (!anchorEl || !isVisible) return null;

        return (
          <Popper
            open
            anchorEl={anchorEl}
            placement='bottom-start'
            style={{ zIndex: 1300 }}
            modifiers={{
              flip: {
                enabled: true,
              },
              preventOverflow: {
                enabled: true,
                boundariesElement: 'viewport',
              },
            }}>
            <Paper elevation={8} style={{ maxWidth: 300, minWidth: 250 }}>
              <MenuList
                dense
                style={{
                  maxHeight: '240px',
                  overflowY: 'auto',
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#c1c1c1 #f1f1f1',
                }}>
                {query === 'disabled' ? (
                  <MenuItem disabled>
                    <Box display='flex' alignItems='center' width='100%'>
                      <Typography variant='body2' color='textSecondary'>
                        We do not support mentions in this type of note
                      </Typography>
                    </Box>
                  </MenuItem>
                ) : isLoading ? (
                  <MenuItem disabled>
                    <Box display='flex' alignItems='center' width='100%'>
                      <Typography variant='body2' color='textSecondary'>
                        Searching...
                      </Typography>
                    </Box>
                  </MenuItem>
                ) : query.length < minChars ? (
                  <MenuItem disabled>
                    <Box display='flex' alignItems='center' width='100%'>
                      <Typography variant='body2' color='textSecondary'>
                        Type to see mentions
                      </Typography>
                    </Box>
                  </MenuItem>
                ) : options.length === 0 ? (
                  <MenuItem disabled>
                    <Box display='flex' alignItems='center' width='100%'>
                      <Typography variant='body2' color='textSecondary'>
                        Nothing found
                      </Typography>
                    </Box>
                  </MenuItem>
                ) : (
                  options.map((opt, i) => (
                    <MenuItem
                      key={opt.key}
                      ref={opt.setRefElement}
                      selected={i === selectedIndex}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        selectOptionAndCleanUp(opt);
                      }}
                      style={{
                        padding: '8px 16px',
                        minHeight: 'auto',
                      }}>
                      <Box display='flex' alignItems='center' width='100%'>
                        <Box mr={1} display='flex' alignItems='center'>
                          {renderTypeIcon(opt.item.type)}
                        </Box>
                        <Box flex={1} minWidth={0}>
                          <Typography
                            variant='body2'
                            style={{
                              fontWeight: 500,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: '100%',
                            }}>
                            @{opt.item.displayName?.replace?.(/_+/g, ' ') || ''}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))
                )}
              </MenuList>
            </Paper>
          </Popper>
        );
      }}
    />
  );
}

export default MentionPlugin;
